import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import Modal from '../common/Modal';
import { analyticsService } from '../../services/api/knowledge/analyticsService';
import { GASummary, GADailyMetric, GATrafficSource, GAMetricsParams } from '../../types/analytics';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface GADashboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: number;
}

type PeriodOption = 7 | 30 | 90;

const GADashboardModal: React.FC<GADashboardModalProps> = ({
  isOpen,
  onClose,
  productId,
}) => {
  const [period, setPeriod] = useState<PeriodOption>(30);
  const [summary, setSummary] = useState<GASummary | null>(null);
  const [metrics, setMetrics] = useState<GADailyMetric[]>([]);
  const [trafficSources, setTrafficSources] = useState<GATrafficSource[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [showCustomDateRange, setShowCustomDateRange] = useState<boolean>(false);

  // Load data when modal opens or period changes
  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, period]);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      // Calculate date range based on period (ending yesterday due to GA data delay)
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const endDate = yesterday.toISOString().split('T')[0];

      const startDateObj = new Date();
      startDateObj.setDate(startDateObj.getDate() - period);
      const startDate = startDateObj.toISOString().split('T')[0];

      const summaryParams: GAMetricsParams = {
        period: period === 7 ? '7days' : period === 30 ? '30days' : '90days'
      };

      const metricsParams: GAMetricsParams = {
        start_date: startDate,
        end_date: endDate
      };

      // Fetch all data in parallel
      const [summaryData, metricsData, trafficData] = await Promise.all([
        analyticsService.getSummary(productId, summaryParams),
        analyticsService.getMetrics(productId, metricsParams),
        analyticsService.getTrafficSources(productId, metricsParams),
      ]);

      setSummary(summaryData);
      setMetrics(metricsData);
      setTrafficSources(trafficData);
    } catch (err: any) {
      console.error('Failed to load GA dashboard data:', err);
      const errorMessage = err.response?.data?.message || err.message || 'データの読み込みに失敗しました';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setError('');

    try {
      // Trigger sync for the last 30 days (ending yesterday, as GA data is typically 1-2 days delayed)
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const endDate = yesterday.toISOString().split('T')[0];

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const startDate = thirtyDaysAgo.toISOString().split('T')[0];

      await analyticsService.syncData(productId, { start_date: startDate, end_date: endDate });

      // Reload data after sync
      await loadData();
    } catch (err: any) {
      console.error('Failed to sync GA data:', err);
      const errorMessage = err.response?.data?.message || err.message || 'データの同期に失敗しました';
      setError(errorMessage);
    } finally {
      setSyncing(false);
    }
  };

  const handleCustomSync = async () => {
    if (!customStartDate || !customEndDate) {
      setError('開始日と終了日を両方指定してください');
      return;
    }

    const start = new Date(customStartDate);
    const end = new Date(customEndDate);

    if (start > end) {
      setError('開始日は終了日より前である必要があります');
      return;
    }

    setSyncing(true);
    setError('');

    try {
      await analyticsService.syncData(productId, {
        start_date: customStartDate,
        end_date: customEndDate
      });

      // Reload data after sync
      await loadData();

      // Clear custom dates and hide form
      setCustomStartDate('');
      setCustomEndDate('');
      setShowCustomDateRange(false);
    } catch (err: any) {
      console.error('Failed to sync GA data:', err);
      const errorMessage = err.response?.data?.message || err.message || '同期に失敗しました';
      setError(errorMessage);
    } finally {
      setSyncing(false);
    }
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString('ja-JP');
  };

  const formatPercentage = (num: number): string => {
    return num.toFixed(2) + '%';
  };

  // Prepare chart data
  const chartData = {
    labels: metrics.map(m => {
      const date = new Date(m.date);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    }),
    datasets: [
      {
        label: 'ページビュー',
        data: metrics.map(m => m.page_views),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'ユニークユーザー',
        data: metrics.map(m => m.unique_users),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'セッション',
        data: metrics.map(m => m.sessions),
        borderColor: 'rgb(234, 179, 8)',
        backgroundColor: 'rgba(234, 179, 8, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Google Analytics ダッシュボード">
      <div className="ga-dashboard" style={{ minHeight: '600px' }}>
        {/* Period Selector and Sync Button */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            {([7, 30, 90] as PeriodOption[]).map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  border: period === p ? '2px solid #2563eb' : '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: period === p ? '#eff6ff' : 'white',
                  color: period === p ? '#2563eb' : '#374151',
                  fontWeight: period === p ? 600 : 400,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                }}
              >
                {p}日間
              </button>
            ))}
          </div>
          <button
            onClick={handleSync}
            disabled={loading || syncing}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: syncing ? '#9ca3af' : '#2563eb',
              color: 'white',
              cursor: loading || syncing ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}
          >
            {syncing ? '同期中...' : '手動同期'}
          </button>
        </div>

        {/* Custom Date Range Sync */}
        <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showCustomDateRange ? '12px' : '0' }}>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#374151' }}>
              期間を指定してデータを取得
            </h4>
            <button
              onClick={() => setShowCustomDateRange(!showCustomDateRange)}
              disabled={loading || syncing}
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: '#374151',
                cursor: loading || syncing ? 'not-allowed' : 'pointer',
                fontSize: '13px',
              }}
            >
              {showCustomDateRange ? '閉じる' : '開く'}
            </button>
          </div>

          {showCustomDateRange && (
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>
                  開始日
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  disabled={loading || syncing}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>
                  終了日
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  disabled={loading || syncing}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <button
                onClick={handleCustomSync}
                disabled={loading || syncing || !customStartDate || !customEndDate}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: (loading || syncing || !customStartDate || !customEndDate) ? '#9ca3af' : '#10b981',
                  color: 'white',
                  cursor: (loading || syncing || !customStartDate || !customEndDate) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                }}
              >
                {syncing ? '取得中...' : '取得'}
              </button>
            </div>
          )}
        </div>

        {error && (
          <div style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            color: '#c33'
          }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            データを読み込み中...
          </div>
        ) : summary ? (
          <>
            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
              <div style={{
                padding: '16px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#f9fafb'
              }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>ページビュー</div>
                <div style={{ fontSize: '24px', fontWeight: 600, color: '#111827' }}>
                  {formatNumber(summary.total_page_views)}
                </div>
              </div>
              <div style={{
                padding: '16px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#f9fafb'
              }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>ユニークユーザー</div>
                <div style={{ fontSize: '24px', fontWeight: 600, color: '#111827' }}>
                  {formatNumber(summary.total_unique_users)}
                </div>
              </div>
              <div style={{
                padding: '16px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#f9fafb'
              }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>セッション</div>
                <div style={{ fontSize: '24px', fontWeight: 600, color: '#111827' }}>
                  {formatNumber(summary.total_sessions)}
                </div>
              </div>
              <div style={{
                padding: '16px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#f9fafb'
              }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>コンバージョン</div>
                <div style={{ fontSize: '24px', fontWeight: 600, color: '#111827' }}>
                  {formatNumber(summary.total_conversions)}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  CVR: {formatPercentage(summary.avg_conversion_rate)}
                </div>
              </div>
            </div>

            {/* Metrics Chart */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>トレンド</h3>
              <div style={{ height: '300px' }}>
                <Line data={chartData} options={chartOptions} />
              </div>
            </div>

            {/* Traffic Sources Table */}
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>トラフィックソース (Top 10)</h3>
              {summary.top_traffic_sources.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>ソース / メディア</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>セッション</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>割合</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.top_traffic_sources.map((source, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '12px' }}>{source.source_medium}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{formatNumber(source.sessions)}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>
                            {((source.sessions / summary.total_sessions) * 100).toFixed(2)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>
                  トラフィックソースのデータがありません
                </div>
              )}
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            データがありません
          </div>
        )}
      </div>
    </Modal>
  );
};

export default GADashboardModal;
